import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { graphql } from 'react-apollo';

import {
  AlbumFolderForContributorShare,
  AlbumForContributorShare,
} from '../../graphql/queries/contributors';
import { AUTH_TYPE } from '../../constants/auth';
import FullScreenLoader from '../global/FullScreenLoader';
import { ALBUM_TYPE } from '../../constants/albums';

/**
 * React HOC For Auto Contributor Authentication.
 *
 * @param {React.Component} WrappedComponent Component to be wrapped.
 * @param {Object} options Options object.
 * @param {AUTH_TYPE} options.authType Type of auth to get.
 * @param {string} options.redirectUrl Custom auth URL in case auth is required.
 * @param {Array} options.skippedAlbumTypes The album subtypes for which to disregard auth
 */
const WithAutoContributorAuthenticationHOC = (WrappedComponent, options = {}) =>
  class extends Component {
    static propTypes = {
      authenticated: PropTypes.bool.isRequired,
      loadingAlbum: PropTypes.bool,
      loadingAlbumFolder: PropTypes.bool,
      album: PropTypes.shape({
        id: PropTypes.string,
        album_type: PropTypes.oneOf(Object.values(ALBUM_TYPE)),
      }),
      albumFolder: PropTypes.shape({
        id: PropTypes.string,
        album_type: PropTypes.oneOf(Object.values(ALBUM_TYPE)),
      }),
      location: PropTypes.object,
      staticContext: PropTypes.object,
    };

    render() {
      const {
        authenticated,
        location,
        staticContext,
        album,
        albumFolder,
        loadingAlbum,
        loadingAlbumFolder,
      } = this.props;

      const { skippedAlbumTypes = [] } = options;

      if (loadingAlbum || loadingAlbumFolder) {
        return <FullScreenLoader />;
      }

      const entity = album ?? albumFolder;

      const authUrl =
        options.authType === AUTH_TYPE.PHONE
          ? '/auth/phone'
          : '/shutterbug/signin';

      // Set referrer so that we can redirect the user
      // at the previously requested page (server side)
      // This is then caught on server's `context` (provided by
      // StaticRouter and passed along to Express's redirect handler
      // (see `react-server-renderer.js`)
      if (
        staticContext &&
        !authenticated &&
        !skippedAlbumTypes.includes(entity?.album_type)
      ) {
        staticContext.referrer = location;
        staticContext.status = 401;
      }

      return authenticated || skippedAlbumTypes.includes(entity?.album_type) ? (
        <WrappedComponent {...this.props} />
      ) : (
        <Redirect to={options.redirectUrl || authUrl} />
      );
    }
  };

const WithAutoContributorAuthentication = (WrappedComponent, options) => {
  const withAutoControbutorAuthentication =
    WithAutoContributorAuthenticationHOC(WrappedComponent, options);
  const withAlbumForContributorShare = graphql(AlbumForContributorShare, {
    options: ({ match }) => ({
      variables: {
        id: match.params.entityId,
      },
    }),
    props: ({ data: { album, loading } }) => ({
      album,
      loadingAlbum: loading,
    }),
  });

  const withAlbumFolderForContributorShare = graphql(
    AlbumFolderForContributorShare,
    {
      options: ({ match }) => ({
        variables: {
          id: match.params.entityId,
        },
      }),
      props: ({ data: { albumFolder, loading } }) => ({
        albumFolder,
        loadingAlbumFolder: loading,
      }),
    },
  );

  const mapStateToProps = ({ auth }) => ({
    authenticated: auth.authenticated,
  });

  return compose(
    withRouter,
    connect(mapStateToProps),
    withAlbumForContributorShare,
    withAlbumFolderForContributorShare,
  )(withAutoControbutorAuthentication);
};

export { WithAutoContributorAuthenticationHOC };

export default WithAutoContributorAuthentication;
